// Data layer for the Penetration Report V2 preview page.
//
// SEED-VS-LIVE BOUNDARY: `fetchMarketRecords` / `fetchTrendSeries` are the only
// two functions that know about seed data. When the real backend for this
// dashboard exists, replace their bodies with `graphqlClient.request(...)` calls
// (see penetrationReportService.ts for the established pattern) returning the
// same `MarketRecord[]` / `TrendPoint[]` shapes. Every pure aggregation helper
// below operates on that typed shape, not on any transport format, so none of
// them — nor the store, nor any component — should need to change.

import { SEED_MARKET_RECORDS, LONG_TERM_TARGET_PCT, createRng } from './seedData';
import { shiftPeriod, formatTrendPointLabel } from './periodUtils';
import {
  ALL_FILTER_VALUE,
  BreakdownDimension,
  BreakdownDimensionType,
  BreakdownRow,
  HeroMetrics,
  MarketRecord,
  PenetrationV2Filters,
  Period,
  SignalTileData,
  SignalTileKey,
  SignalTileKeyType,
  Solution,
  SolutionType,
  TrendPoint,
} from './types';

export async function fetchMarketRecords(): Promise<MarketRecord[]> {
  return SEED_MARKET_RECORDS;
}

export async function fetchTrendSeries(
  asOfPeriod: Period,
  comparePeriod: Period,
  rangeMonths: number,
): Promise<TrendPoint[]> {
  return generateTrendSeries(SEED_MARKET_RECORDS, asOfPeriod, comparePeriod, rangeMonths);
}

function clampPct(value: number): number {
  return Math.min(100, Math.max(0, value));
}

function absoluteMonthIndex(period: Period): number {
  return period.year * 12 + period.month;
}

/**
 * There's no real month-by-month history in the seed dataset, so each
 * calendar period is given a small deterministic swing (seeded by that exact
 * year+month) around the dataset's overall rate — reproducible on reload, but
 * visibly different when the user picks a different "As of"/"Compare with"
 * month, instead of every period rendering an identical line.
 */
function periodAnchorPct(basePct: number, period: Period): number {
  const rng = createRng(absoluteMonthIndex(period));
  return clampPct(basePct + (rng() - 0.5) * 6);
}

/**
 * Builds `rangeMonths` points for the trend chart. The "current" line counts
 * back from `asOfPeriod` and the "prior" line counts back from `comparePeriod`
 * independently — the two anchors don't need to be exactly one year apart —
 * and each line's endpoint (and therefore its whole shape) is anchored to its
 * own period via `periodAnchorPct`.
 */
function generateTrendSeries(
  records: MarketRecord[],
  asOfPeriod: Period,
  comparePeriod: Period,
  rangeMonths: number,
): TrendPoint[] {
  const total = records.length || 1;
  const baseCurrentPct = (records.filter((r) => r.isCustomerAsOf).length / total) * 100;
  const basePriorPct = (records.filter((r) => r.isCustomerPrior).length / total) * 100;
  const currentEnd = periodAnchorPct(baseCurrentPct, asOfPeriod);
  const priorEnd = periodAnchorPct(basePriorPct, comparePeriod);

  const rng = createRng(absoluteMonthIndex(asOfPeriod) + absoluteMonthIndex(comparePeriod) + rangeMonths);
  const spread = Math.min(6, rangeMonths * 0.3);
  const includeYear = rangeMonths > 12;

  return Array.from({ length: rangeMonths }, (_, i) => {
    const offsetFromEnd = rangeMonths - 1 - i;
    const progress = rangeMonths === 1 ? 1 : i / (rangeMonths - 1);
    const noise = (rng() - 0.5) * 0.3;

    return {
      monthLabel: formatTrendPointLabel(shiftPeriod(asOfPeriod, -offsetFromEnd), includeYear),
      priorMonthLabel: formatTrendPointLabel(shiftPeriod(comparePeriod, -offsetFromEnd), includeYear),
      currentPct: clampPct(currentEnd - spread * (1 - progress) + noise),
      priorPct: clampPct(priorEnd - spread * (1 - progress) + noise),
    };
  });
}

const SIGNAL_FLAG: Record<SignalTileKeyType, keyof Pick<MarketRecord, 'highCrime' | 'highPurchekConcentration' | 'cartOrdinance'>> = {
  [SignalTileKey.HIGH_CRIME]: 'highCrime',
  [SignalTileKey.HIGH_PURCHEK_CONCENTRATION]: 'highPurchekConcentration',
  [SignalTileKey.CART_ORDINANCE]: 'cartOrdinance',
};

const SIGNAL_TILE_LABEL: Record<SignalTileKeyType, string> = {
  [SignalTileKey.HIGH_CRIME]: 'High-crime market',
  [SignalTileKey.HIGH_PURCHEK_CONCENTRATION]: 'High Purchek concentration',
  [SignalTileKey.CART_ORDINANCE]: 'Cart-ordinance market',
};

const BREAKDOWN_LABEL: Record<BreakdownDimensionType, string> = {
  [BreakdownDimension.BRAND]: 'Brand',
  [BreakdownDimension.SOLUTION]: 'Solution',
  [BreakdownDimension.CHANNEL]: 'Channel',
  [BreakdownDimension.STATE]: 'State',
};

/** Applies the top brand/channel/solution filters and (if set) the selected signal tile. */
export function filterMarketRecords(
  records: MarketRecord[],
  filters: PenetrationV2Filters,
  signalTile: SignalTileKeyType | null,
): MarketRecord[] {
  return records.filter((record) => {
    if (filters.brand !== ALL_FILTER_VALUE && record.brand !== filters.brand) return false;
    if (filters.channel !== ALL_FILTER_VALUE && record.channel !== filters.channel) return false;
    if (filters.solution !== ALL_FILTER_VALUE && !record.solutionsInstalled.includes(filters.solution)) return false;
    if (signalTile && !record[SIGNAL_FLAG[signalTile]]) return false;
    return true;
  });
}

function pctDelta(currentPct: number, priorPct: number) {
  return Math.round((currentPct - priorPct) * 10) / 10;
}

export function computeHeroMetrics(records: MarketRecord[]): HeroMetrics {
  const targetCount = records.length;
  const acquiredCount = records.filter((r) => r.isCustomerAsOf).length;
  const priorAcquiredCount = records.filter((r) => r.isCustomerPrior).length;
  const currentPct = targetCount > 0 ? (acquiredCount / targetCount) * 100 : 0;
  const priorPct = targetCount > 0 ? (priorAcquiredCount / targetCount) * 100 : 0;

  return {
    currentPct,
    priorPct,
    deltaPct: pctDelta(currentPct, priorPct),
    acquiredCount,
    targetCount,
    remainingCount: targetCount - acquiredCount,
    longTermTargetPct: LONG_TERM_TARGET_PCT,
  };
}

export function computeSignalTiles(records: MarketRecord[]): SignalTileData[] {
  return (Object.values(SignalTileKey) as SignalTileKeyType[]).map((key) => {
    const flag = SIGNAL_FLAG[key];
    const matching = records.filter((r) => r[flag]);
    const targetCount = matching.length;
    const acquiredCount = matching.filter((r) => r.isCustomerAsOf).length;
    const priorAcquiredCount = matching.filter((r) => r.isCustomerPrior).length;
    const currentPct = targetCount > 0 ? (acquiredCount / targetCount) * 100 : 0;
    const priorPct = targetCount > 0 ? (priorAcquiredCount / targetCount) * 100 : 0;

    return {
      key,
      label: SIGNAL_TILE_LABEL[key],
      currentPct,
      priorPct,
      deltaPct: pctDelta(currentPct, priorPct),
      acquiredCount,
    };
  });
}

function buildBreakdownRow(key: string, label: string, group: MarketRecord[]): BreakdownRow {
  const targetCount = group.length;
  const acquiredCount = group.filter((r) => r.isCustomerAsOf).length;
  const priorAcquiredCount = group.filter((r) => r.isCustomerPrior).length;
  const currentPct = targetCount > 0 ? (acquiredCount / targetCount) * 100 : 0;
  const priorPct = targetCount > 0 ? (priorAcquiredCount / targetCount) * 100 : 0;

  return {
    key,
    label,
    currentPct,
    priorPct,
    deltaPct: pctDelta(currentPct, priorPct),
    acquiredCount,
    targetCount,
  };
}

/**
 * Groups the already-filtered records by the active breakdown tab.
 * Brand/Channel/State rows are "% of all target locations in that group that
 * are customers today". Solution rows are attach-rate among existing customers
 * (the mockup's Solution table denominator matches the acquired total, not the
 * full addressable market), so that tab groups by installed solution instead.
 */
export function aggregateBreakdown(records: MarketRecord[], dimension: BreakdownDimensionType): BreakdownRow[] {
  if (dimension === BreakdownDimension.SOLUTION) {
    const customers = records.filter((r) => r.isCustomerAsOf || r.isCustomerPrior);
    return (Object.values(Solution) as SolutionType[]).map((solution) => {
      const targetCount = customers.length;
      const acquiredCount = customers.filter((r) => r.solutionsInstalled.includes(solution)).length;
      const priorAcquiredCount = customers.filter((r) => r.solutionsInstalledPrior.includes(solution)).length;
      const currentPct = targetCount > 0 ? (acquiredCount / targetCount) * 100 : 0;
      const priorPct = targetCount > 0 ? (priorAcquiredCount / targetCount) * 100 : 0;

      return {
        key: solution,
        label: solution,
        currentPct,
        priorPct,
        deltaPct: pctDelta(currentPct, priorPct),
        acquiredCount,
        targetCount,
      };
    }).sort((a, b) => b.currentPct - a.currentPct);
  }

  const groupKey = (r: MarketRecord) =>
    dimension === BreakdownDimension.BRAND ? r.brand
      : dimension === BreakdownDimension.CHANNEL ? r.channel
        : r.state;

  const groups = new Map<string, MarketRecord[]>();
  records.forEach((record) => {
    const key = groupKey(record);
    const group = groups.get(key);
    if (group) group.push(record); else groups.set(key, [record]);
  });

  return Array.from(groups.entries())
    .map(([key, group]) => buildBreakdownRow(key, key, group))
    .sort((a, b) => b.currentPct - a.currentPct);
}

export function breakdownDimensionLabel(dimension: BreakdownDimensionType): string {
  return BREAKDOWN_LABEL[dimension];
}
